// src/Components/EventWizard/LivekitEventPanel.jsx
//
// Studio's LiveKit (WHIP) launch path — deliberately NOT the DASH pipeline.
// No slate, no warmup prepare/go/ready, no drift/MPD: pick a preset per camera,
// launch ffmpeg, see if it errors. See
// ir-webrtc-server/livekit/ARCHITECTURE_LIVEKIT.md for the LiveKit side.
import React, { useState } from 'react';
import { authFetch } from '../../utils/authFetch';

const API_BASE =
  (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '')) ||
  'http://localhost:4000';

const box = { border: '1px solid #444', borderRadius: 8, padding: 12, marginBottom: 10, background: 'rgba(154,81,226,0.04)' };
const btn = (bg, enabled = true) => ({
  background: enabled ? bg : '#9e9e9e',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '5px 10px',
  fontSize: 12,
  cursor: enabled ? 'pointer' : 'not-allowed',
});
const pill = (bg) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
  fontWeight: 600, color: '#fff', background: bg,
});

function ingressStatePill(state) {
  const status = state?.status || state?.state?.status;
  if (!status) return <span style={pill('#9e9e9e')}>no status</span>;
  const map = {
    ENDPOINT_PUBLISHING: '#4caf50',
    ENDPOINT_BUFFERING: '#ff9800',
    ENDPOINT_ERROR: '#f44336',
    ENDPOINT_INACTIVE: '#9e9e9e',
  };
  return <span style={pill(map[status] || '#9e9e9e')}>{status.replace('ENDPOINT_', '').toLowerCase()}</span>;
}

// The track info a viewer would actually receive — what `GET /api/rooms/:name/participants`
// confirms beyond the ingress state pill (which only says the ingress connected,
// not that the room has real video flowing).
function ParticipantInfo({ participant }) {
  if (!participant) return <span style={{ fontSize: 11, color: '#888' }}>not in room yet</span>;
  const track = (participant.tracks || []).find(t => t.type === 'VIDEO');
  return (
    <span style={{ fontSize: 11, color: '#4caf50' }}>
      {participant.state} in room
      {track && ` — ${track.mimeType} ${track.width}x${track.height}`}
    </span>
  );
}

export default function LivekitEventPanel({ evt, camsOnly, presets, api, ingressMap, participantsMap, onRefreshIngress, setMessage }) {
  const [rowState, setRowState] = useState({}); // { [instanceId]: { presetId, busy, error, logOpen, logText } }

  const patchRow = (id, patch) =>
    setRowState(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const livekitPresets = (presets || []).filter(p => (p.command || '').includes('{{LIVEKIT_URL}}'));

  async function createIngress(inst) {
    patchRow(inst.instanceId, { busy: true, error: '' });
    try {
      await api.createLivekitIngress(evt, inst.camName);
      await onRefreshIngress?.();
    } catch (e) {
      patchRow(inst.instanceId, { error: e.response?.data?.message || e.message });
    } finally {
      patchRow(inst.instanceId, { busy: false });
    }
  }

  async function deleteIngress(inst) {
    if (!window.confirm(`Delete the LiveKit ingress for "${inst.camName}"? A new one will get a different stream key.`)) return;
    patchRow(inst.instanceId, { busy: true, error: '' });
    try {
      await api.deleteLivekitIngress(evt, inst.camName);
      await onRefreshIngress?.();
    } catch (e) {
      patchRow(inst.instanceId, { error: e.response?.data?.message || e.message });
    } finally {
      patchRow(inst.instanceId, { busy: false });
    }
  }

  async function applyPreset(inst) {
    const presetId = rowState[inst.instanceId]?.presetId;
    if (!presetId) return;
    patchRow(inst.instanceId, { busy: true, error: '' });
    try {
      await api.applyLivekitPreset(evt, {
        instanceId: inst.instanceId,
        camName: inst.camName,
        presetId,
        publicIp: inst.publicIp,
      });
      setMessage?.(`Preset applied to ${inst.camName}`);
    } catch (e) {
      patchRow(inst.instanceId, { error: e.response?.data?.message || e.message });
    } finally {
      patchRow(inst.instanceId, { busy: false });
    }
  }

  // These hit the instance's dedicated LiveKit slot (/livekit/*), NOT the DASH
  // ffmpeg slot — both can run at once and neither's stop touches the other.
  // Requires the instance to be on the "dev" controller channel until the
  // LiveKit-slot controller is promoted to stable.
  async function start(inst) {
    patchRow(inst.instanceId, { busy: true, error: '' });
    try {
      const r = await api.livekitStart(inst.instanceId);
      if (r.data?.error) throw new Error(r.data.error);
      patchRow(inst.instanceId, { started: true });
      setMessage?.(`LiveKit ffmpeg started on ${inst.camName}`);
    } catch (e) {
      patchRow(inst.instanceId, { error: e.response?.data?.error || e.response?.data?.message || e.message });
    } finally {
      patchRow(inst.instanceId, { busy: false });
    }
  }

  async function stop(inst) {
    patchRow(inst.instanceId, { busy: true, error: '' });
    try {
      await api.livekitStop(inst.instanceId);
      patchRow(inst.instanceId, { started: false });
      setMessage?.(`LiveKit ffmpeg stopped on ${inst.camName}`);
    } catch (e) {
      patchRow(inst.instanceId, { error: e.response?.data?.error || e.response?.data?.message || e.message });
    } finally {
      patchRow(inst.instanceId, { busy: false });
    }
  }

  async function toggleLog(inst) {
    const cur = rowState[inst.instanceId] || {};
    if (cur.logOpen) { patchRow(inst.instanceId, { logOpen: false }); return; }
    patchRow(inst.instanceId, { logOpen: true, logText: 'Loading…' });
    try {
      const res = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/livekit/log`);
      const text = res.status === 204 ? '(empty — process not running or no output yet)' : await res.text();
      patchRow(inst.instanceId, { logText: text });
    } catch {
      patchRow(inst.instanceId, { logText: 'Log fetch error' });
    }
  }

  const [refreshing, setRefreshing] = useState(false);
  async function refreshAll() {
    setRefreshing(true);
    try { await onRefreshIngress?.(); } finally { setRefreshing(false); }
  }

  const [testToken, setTestToken] = useState(null); // { token, wsUrl, identity }
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState('');
  async function createTestToken() {
    setTokenBusy(true);
    setTokenError('');
    try {
      const r = await api.createLivekitTestToken(evt);
      setTestToken(r.data);
    } catch (e) {
      setTokenError(e.response?.data?.message || e.message);
    } finally {
      setTokenBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#999' }}>
          LiveKit path: no slate, no warmup, no drift/MPD monitoring — that stays DASH-only for now.
          Create an ingress per camera, apply a LiveKit preset, launch ffmpeg, watch the log.
          Ingress state, room participants and viewer count refresh automatically every 5s.
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
          <button style={btn('#9a51e2', !tokenBusy)} disabled={tokenBusy} onClick={createTestToken}>
            {tokenBusy ? 'Creating…' : 'Create test token'}
          </button>
          <button style={btn('#555', !refreshing)} disabled={refreshing} onClick={refreshAll}>
            {refreshing ? 'Refreshing…' : 'Refresh status'}
          </button>
        </div>
      </div>

      {/* Viewers = room participants that are not camera ingresses. The server
          API doesn't say WHICH camera each viewer is subscribed to (selective
          subscription is client-side state) — per-camera counts would need the
          player to report it, e.g. via participant metadata. */}
      {/* Count only — who they are lives in the audit log (livekit_token_issued). */}
      {(() => {
        const all = Object.values(participantsMap || {});
        const viewers = all.filter(p => p.kind !== 'INGRESS' && !p.isPublisher);
        return (
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            <b style={{ color: viewers.length ? '#4caf50' : '#888' }}>
              {viewers.length} viewer{viewers.length !== 1 ? 's' : ''} in room
            </b>
          </div>
        );
      })()}

      {tokenError && <div style={{ fontSize: 12, color: '#f44336', marginBottom: 10 }}>{tokenError}</div>}
      {testToken && (
        <div style={{ ...box, background: 'rgba(154,81,226,0.08)' }}>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>
            Test viewer token for room "{evt}" (identity <code>{testToken.identity}</code>, canSubscribe only, expires in 1h).
            Paste into a manual player test — this is not wired to the real player component yet.
          </div>
          <div style={{ marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#888' }}>wsUrl: </span>
            <code style={{ fontSize: 11, background: 'rgba(0,0,0,0.25)', padding: '2px 6px', borderRadius: 4 }}>{testToken.wsUrl}</code>
          </div>
          <div>
            <span style={{ fontSize: 11, color: '#888' }}>token: </span>
            <code style={{ fontSize: 11, background: 'rgba(0,0,0,0.25)', padding: '2px 6px', borderRadius: 4, wordBreak: 'break-all' }}>{testToken.token}</code>
          </div>
        </div>
      )}

      {camsOnly.length === 0 && (
        <div style={{ color: '#999', fontSize: 13 }}>No cameras launched for this event yet.</div>
      )}
      {camsOnly.map(inst => {
        const ingress = ingressMap?.[inst.camName];
        const participant = participantsMap?.[inst.camName];
        const rs = rowState[inst.instanceId] || {};
        // The DASH "running" monitor (rtmp-monitor.json) checks for DASH segment
        // output, which this path never produces — so it isn't a valid signal
        // here. Default from the last known ingress/participant state instead,
        // then let explicit Start/Stop clicks override it.
        const liveSignal = participant?.state === 'ACTIVE'
          || ['ENDPOINT_PUBLISHING', 'ENDPOINT_BUFFERING'].includes(ingress?.state?.status);
        const running = rs.started ?? liveSignal;
        return (
          <div key={inst.instanceId} style={box}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <strong>{inst.camName}</strong>
              <span style={{ fontSize: 11, color: '#888' }}>{inst.publicIp}</span>
              <span style={pill(running ? '#4caf50' : '#9e9e9e')} title="Local/best-guess — the ingress and room pills to the right are the real confirmation">
                {running ? 'launched' : 'not launched'}
              </span>
              {ingress && ingressStatePill(ingress.state)}
              <ParticipantInfo participant={participant} />
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button style={btn('#3f51b5', !running)} disabled={running || rs.busy} onClick={() => start(inst)}>Start</button>
                <button style={btn('#f44336', running)} disabled={!running || rs.busy} onClick={() => stop(inst)}>Stop</button>
                <button style={btn('#555')} onClick={() => toggleLog(inst)}>{rs.logOpen ? 'Hide log' : 'Log'}</button>
              </div>
            </div>

            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {!ingress?.publishUrl ? (
                <button style={btn('#9a51e2', !rs.busy)} disabled={rs.busy} onClick={() => createIngress(inst)}>
                  Create LiveKit ingress
                </button>
              ) : (
                <>
                  <span style={{ fontSize: 11, color: '#888' }}>Publish URL:</span>
                  <code style={{ fontSize: 11, background: 'rgba(0,0,0,0.25)', padding: '2px 6px', borderRadius: 4 }}>
                    {ingress.publishUrl}
                  </code>
                  <button style={btn('#555', !rs.busy)} disabled={rs.busy} onClick={() => deleteIngress(inst)}>
                    Delete ingress
                  </button>
                </>
              )}
            </div>

            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <select
                value={rs.presetId || ''}
                onChange={e => patchRow(inst.instanceId, { presetId: e.target.value })}
                style={{ padding: 4, borderRadius: 4, background: '#181828', color: '#fff', border: '1px solid #555', fontSize: 12 }}
              >
                <option value="">— Select LiveKit preset —</option>
                {livekitPresets.map(p => (
                  <option key={p._id} value={p._id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>
                ))}
              </select>
              <button
                style={btn('#4caf50', !!rs.presetId && !!ingress?.publishUrl && !rs.busy)}
                disabled={!rs.presetId || !ingress?.publishUrl || rs.busy}
                onClick={() => applyPreset(inst)}
                title={!ingress?.publishUrl ? 'Create the ingress first' : ''}
              >
                Apply preset
              </button>
              {livekitPresets.length === 0 && (
                <span style={{ fontSize: 11, color: '#ff9800' }}>
                  No preset has a {'{{LIVEKIT_URL}}'} placeholder yet — create one in FFmpeg Presets.
                </span>
              )}
            </div>

            {rs.error && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#f44336' }}>{rs.error}</div>
            )}
            {rs.logOpen && (
              <pre style={{
                marginTop: 8, maxHeight: 220, overflow: 'auto', fontSize: 11,
                background: 'rgba(0,0,0,0.35)', padding: 8, borderRadius: 4, whiteSpace: 'pre-wrap',
              }}>
                {rs.logText}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
